// taxe-parc.js — le barème de taxe de séjour de TOUT le parc, d'un coup
// (demande Dimitri 29/09, « les dix logements d'un coup »). Module PUR.
//
// LECTURE SEULE. Ce module ne propose, ne pré-remplit et n'invente AUCUN
// montant : les tarifs viennent des délibérations des collectivités et de
// l'espace hébergeur de Dimitri. Il dit, logement par logement, ce qui est
// RÉELLEMENT appliqué aujourd'hui (le même prédicat que le devis public :
// `resumeBareme` / `hasValidBareme`) et à quelle commune l'adresse rattache
// le logement — rien de plus.
//
// ⚠️ La saisie n'est pas ici : enregistrer un barème écrit `log.taxe_sejour`
// dans la configuration, qui entre dans le prix du devis payé en ligne —
// chemin argent, en attente de l'accord de Dimitri.

import { getAllLogsFlat } from "./sousloc-helpers";
import { parseAddressCityCp, normalizeCommuneNom, resumeBareme } from "./taxe-commune-helpers";

/**
 * Une ligne par logement réservable (chambres de la sous-location comprises,
 * dédoublonné par id).
 * @returns {Array<{id, nom, proprio, commune, code_postal, adresseLisible:boolean,
 *   bareme: null | {taux_pct, plafond_eur, mineurs_exoneres, source}}>}
 */
export function apercuTaxeParc(cfg) {
  return getAllLogsFlat(cfg).map((log) => {
    // Une chambre sans adresse propre est à l'adresse de sa maison.
    const adresse =
      (log && log.adresse_complete) ||
      (log && log.parentLog && log.parentLog.adresse_complete) ||
      null;
    const lieu = parseAddressCityCp(adresse);
    return {
      id: log.id,
      nom: log.nom || log.name || log.id,
      proprio: log.propNom || null,
      commune: lieu ? lieu.city : null,
      code_postal: lieu ? lieu.postal_code : null,
      adresseLisible: Boolean(lieu),
      bareme: resumeBareme(log, cfg),
    };
  });
}

/**
 * Regroupe les lignes par commune (une délibération = une commune) ; les
 * adresses illisibles forment leur propre groupe, à la fin, nommé comme tel.
 *
 * La clé est la commune SEULE, normalisée (casse, accents, tirets, espaces) :
 * une commune a plusieurs codes postaux (Toulouse : 31000, 31200, 31500…) mais
 * une seule délibération — la couper par code postal obligeait à saisir trois
 * fois le même barème. Les codes postaux rencontrés sont gardés pour l'en-tête.
 */
export function grouperParCommune(lignes) {
  const groupes = new Map();
  for (const l of lignes) {
    const nomNormalise = l.commune ? normalizeCommuneNom(l.commune) : "";
    const cle = nomNormalise || "__illisible__";
    if (!groupes.has(cle)) {
      groupes.set(cle, {
        cle,
        commune: nomNormalise ? l.commune : null,
        codes_postaux: [],
        logements: [],
      });
    }
    const g = groupes.get(cle);
    if (l.code_postal && !g.codes_postaux.includes(l.code_postal)) {
      g.codes_postaux.push(l.code_postal);
      g.codes_postaux.sort();
    }
    g.logements.push(l);
  }
  return [...groupes.values()].sort((a, b) => {
    if (a.cle === "__illisible__") return 1;
    if (b.cle === "__illisible__") return -1;
    return String(a.commune).localeCompare(String(b.commune), "fr");
  });
}

/** Les compteurs de tête : combien de logements ont un barème appliqué. */
export function compterTaxeParc(lignes) {
  const avec = lignes.filter((l) => l.bareme).length;
  return { total: lignes.length, avec, sans: lignes.length - avec };
}
